import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { useSelector } from 'react-redux';
import { diffItemAgainstPublic, mergePublicAndPrivateItems } from '../common/ItemTools';
import { selectResolveShareTags } from '../redux/features/tags/tagsSlice';
import { CARD_SHADOW_BLEED, shadowBleed } from './common/cardShadow';
import ItemCard from './ItemCard';
import FitGrid from './common/grid/FitGrid';
import CardRow from './common/grid/CardRow';
import CardGridBody from './common/grid/CardGridBody';
import { cardInnerSx } from './common/hero/styles';
import useGridLayout from '../hooks/useGridLayout';

// Height-bounded card grid that fits a page of items into the available
// space: square cards sized by container queries. Two modes:
// - With an explicit `arrangement` (e.g. the author hero page): a fixed
//   cols × rows CSS grid, flush with the parent's side edges, cards packed
//   from the top-left at full-grid cell size no matter how few items there
//   are.
// - Without (the item views): the centered layout, with its ≤3-items special
//   case and two-row card cap, arranged per the shared viewport-based layout
//   (useGridLayout).
export default function FitItemGrid(props) {
  const {
    items, namespace, itemActions, isPreview, arrangement,
  } = props;

  const layout = useGridLayout();
  const preview = isPreview == null ? namespace === 'public' : isPreview;

  const resolveShareTags = useSelector(selectResolveShareTags);
  const merged = items.map((item) => {
    const card = mergePublicAndPrivateItems(item);
    if (card) {
      card.shareDiff = diffItemAgainstPublic(item, resolveShareTags(item.privateData?.tagIds));
    }
    return card;
  });

  const card = (item) => (
    <ItemCard
      isPreview={preview}
      item={item}
      namespace={namespace}
      {...itemActions}
    />
  );

  if (arrangement) {
    const { cols, rows } = arrangement;
    return (
      <Box sx={{
        flex: 1,
        minHeight: 0,
        ...shadowBleed,
        padding: `16px 0 ${CARD_SHADOW_BLEED}`,
      }}>
        <Box sx={{
          width: '100%',
          height: '100%',
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
          gap: '16px',
          // Square cards can be narrower than their cell (they cap at the
          // row height); anchor the first column left and the last column
          // right so the grid's outer edges stay flush, centering the rest.
          ...(cols > 1 && {
            [`& > :nth-of-type(${cols}n + 1)`]: { justifyContent: 'flex-start' },
            [`& > :nth-of-type(${cols}n)`]: { justifyContent: 'flex-end' },
          }),
        }}>
          {merged.map((item) => (
            <Box
              key={item.id}
              sx={{
                containerType: 'size',
                minWidth: 0,
                minHeight: 0,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Box sx={cardInnerSx}>{card(item)}</Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  const lgSpan = Math.max(1, Math.round(12 / layout.arrangement.cols));
  const cardDirection = layout.arrangement.cols === 1 ? 'column' : 'row';

  return (
    <FitGrid>
      {items.length <= 3 ? (
        <CardRow items={merged} direction={cardDirection} renderCard={card} />
      ) : (
          <CardGridBody items={merged} renderCard={card} lgSpan={lgSpan} rowCap={layout.rowCap} />
      )}
    </FitGrid>
  );
}

FitItemGrid.propTypes = {
  items: PropTypes.array.isRequired,
  namespace: PropTypes.string,
  itemActions: PropTypes.object,
  isPreview: PropTypes.bool,
  arrangement: PropTypes.shape({
    cols: PropTypes.number,
    rows: PropTypes.number,
  }),
};

FitItemGrid.defaultProps = {
  namespace: 'public',
  itemActions: {},
  isPreview: null,
  arrangement: null,
};
